import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/* useLayoutEffect in the browser (measure before paint, no flash), a no-op
   useEffect on the server — so the screen test's renderToStaticMarkup draws the
   banner without React's "useLayoutEffect does nothing on the server" warning. */
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
import Icon from './Icon';
import { useApp } from '../store';
import { somethingIsUnsaved, watchUnsaved } from '../unsaved';
import { WHATS_NEW, WHATS_NEW_TITLE, WHATS_NEW_VERSION } from '../whatsnew';
import {
  EVERY,
  NO_SOONER_THAN,
  isWorthOffering,
  versionOf,
  whatIsServed,
} from '../update-check';

/**
 * ONE BANNER ACROSS THE TOP — the update offer and the What's-new tour, merged.
 *
 * WHAT CHANGED, AND WHY. Until 3.20 these were two quiet cards at the foot of the
 * sidebar (ANewVersion #268, WhatsNewCallout). People did not notice them —
 * Vinnie, 15 Oct: *"users don't see the update notification because it is very
 * small."* So they became ONE strip across the top, the same unmissable shape as
 * the environment banner, and merged, because they are two states of a single
 * lifecycle: you are BEHIND (a newer build is served) → you update → you are
 * CURRENT on a release you have not toured. You never see both at once, so one
 * banner shows whichever applies and nothing when neither does.
 *
 *   UPDATE state   — a newer build is on the server. "A new version is ready →
 *                    Update now." PERSISTENT: there is no "Not now". It stays
 *                    until the person actually updates, which is the point —
 *                    Vinnie wanted the notice to persist so people update.
 *   WHAT'S-NEW     — you are on the new build and have not toured it. "What's
 *                    new in X → See what's new", dismissable, with a one-shot
 *                    confetti burst, because arriving on the new version is the
 *                    reward and this is where a celebration belongs.
 *
 * ⚠️ THE PART THAT WOULD DO HARM IF IT WERE WRONG — kept exactly as #268 had it.
 * "Update now" reloads, and a reload throws away anything typed and not saved.
 * So it asks first (somethingIsUnsaved) and, when there is unsaved work, holds
 * up with a line telling the person to finish rather than helpfully discarding
 * their paragraph. The offer comes back on its own once the work is saved.
 *
 * MOTION (R7). The strip drops in on a single shared transform on the app shell
 * (not a height animation), the update icon turns once, a light sheen sweeps,
 * the button carries a slow glow, and What's-new bursts confetti once. ALL of it
 * is off under prefers-reduced-motion — the banner simply appears, still useful.
 *
 * WHAT WAS REMOVED WITH THIS. The "Live updates have stopped" card (#308) is
 * gone — Vinnie, 15 Oct: the page should resume quickly on its own, which the
 * live seam already does on reconnect. Only the visible notice went; the
 * auto-re-read in src/live.ts is untouched.
 */

const seenKey = 'flightdeck-whatsnew-seen';

function tourSeen(): boolean {
  try {
    return localStorage.getItem(seenKey) === WHATS_NEW_VERSION;
  } catch {
    // Private browsing — treat as seen so a tour never nags on every load.
    return true;
  }
}

function markTourSeen() {
  try {
    localStorage.setItem(seenKey, WHATS_NEW_VERSION);
  } catch {
    /* Private browsing — the state below still holds it for this session. */
  }
}

export default function UpdateBanner() {
  const mine = __BUILD_INFO__;
  const setChangelogOpen = useApp((s) => s.setChangelogOpen);
  const setupForced = useApp((s) => s.setupForced);

  /** The newer build the server is serving, or '' while there is none to offer. */
  const [served, setServed] = useState('');
  /** True once Update has been pressed with something unsaved on screen. */
  const [heldUp, setHeldUp] = useState(false);
  /** Whether this person has already met this release's tour. */
  const [seen, setSeen] = useState(tourSeen);

  useEffect(() => {
    let stopped = false;
    let asked = 0;

    const ask = async () => {
      if (stopped || Date.now() - asked < NO_SOONER_THAN) return;
      asked = Date.now();
      const latest = await whatIsServed();
      if (stopped || !isWorthOffering(mine, latest)) return;
      setServed(latest as string);
    };

    void ask();
    const timer = window.setInterval(() => void ask(), EVERY);
    /* And when the tab comes forward — timers stop in a hidden tab, so one left
       overnight would otherwise ask once, hours late. */
    const back = () => {
      if (document.visibilityState === 'visible') void ask();
    };
    document.addEventListener('visibilitychange', back);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', back);
    };
  }, [mine]);

  /* Somebody finishing what they were typing should see the offer come back. */
  useEffect(() => watchUnsaved((n) => { if (n === 0) setHeldUp(false); }), []);

  const updateReady = isWorthOffering(mine, served || null);

  // The update offer wins when both could show — you should land on the newest
  // build before being toured round it. The tour waits for the build you are on.
  if (updateReady) {
    return (
      <UpdateBannerView
        mode="update"
        version={versionOf(served)}
        heldUp={heldUp}
        onPrimary={() => {
          if (somethingIsUnsaved()) {
            setHeldUp(true);
            return;
          }
          window.location.reload();
        }}
      />
    );
  }

  // The tour: once per release, never during forced first-run setup (the welcome
  // owns the screen then, and a second prompt beside it is noise).
  if (!setupForced && !seen && WHATS_NEW.length > 0) {
    return (
      <UpdateBannerView
        mode="whatsnew"
        version={WHATS_NEW_VERSION}
        count={WHATS_NEW.length}
        onPrimary={() => {
          markTourSeen();
          setSeen(true);
          setChangelogOpen(true);
        }}
        onDismiss={() => {
          markTourSeen();
          setSeen(true);
        }}
      />
    );
  }

  return null;
}

/**
 * THE STRIP ITSELF, SEPARATE FROM THE DECIDING — the same separation ANewVersion
 * and LiveUpdates kept, and for the same reason: everything that puts this on
 * screen is a timer, a fetch or a stored flag, which no screen test can reach by
 * an honest route, so the one surface nothing draws is the one nobody reviews.
 * Drawn on its own, both states are reviewed like every other screen.
 */
export function UpdateBannerView({
  mode,
  version,
  count = 0,
  heldUp = false,
  onPrimary,
  onDismiss,
}: {
  mode: 'update' | 'whatsnew';
  version: string;
  count?: number;
  heldUp?: boolean;
  onPrimary: () => void;
  onDismiss?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // THE ENTRANCE. Drop the whole shell in on one transform (R7: transform, not
  // height), measured so the content below eases down by exactly the strip's
  // height. Confetti fires once for What's-new. Both are off under reduced
  // motion — celebrate() no-ops, and the shell drop is simply skipped.
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const shell = el.closest('.app-shell') as HTMLElement | null;
    let reduce = false;
    try {
      reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      /* no matchMedia — allow motion */
    }

    let clearDrop = 0;
    if (shell && !reduce) {
      shell.style.setProperty('--ub-h', `${el.offsetHeight}px`);
      shell.classList.add('ub-entering');
      clearDrop = window.setTimeout(() => shell.classList.remove('ub-entering'), 480);
    }

    let stopConfetti: (() => void) | undefined;
    if (mode === 'whatsnew') {
      // celebrate() itself respects reduced motion.
      void import('../confetti').then((m) => {
        stopConfetti = m.celebrate();
      });
    }

    return () => {
      if (clearDrop) window.clearTimeout(clearDrop);
      shell?.classList.remove('ub-entering');
      stopConfetti?.();
    };
  }, [mode]);

  if (mode === 'update') {
    return (
      <div className="update-banner mode-update" role="status" ref={ref}>
        <span className="ub-sheen" aria-hidden="true" />
        <span className="ub-lead">
          <span className="ub-icon">
            <Icon name="package" size={15} />
          </span>
          A new version is ready
        </span>
        {heldUp ? (
          <span className="ub-note">
            Finish what you are typing first — updating reloads the page, and anything not
            saved would go with it.
          </span>
        ) : (
          <span className="ub-note">Version {version}. Yours keeps working until you update.</span>
        )}
        <span className="ub-do">
          <button className="ub-btn" onClick={onPrimary}>
            Update now
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="update-banner mode-whatsnew" role="status" ref={ref}>
      <span className="ub-sheen" aria-hidden="true" />
      <span className="ub-lead">
        <span className="ub-icon">
          <Icon name="sparkle" size={15} />
        </span>
        {WHATS_NEW_TITLE}
      </span>
      <span className="ub-note">
        {count} {count === 1 ? 'new thing' : 'new things'} to see — take the tour and we&rsquo;ll
        show you where each one lives.
      </span>
      <span className="ub-do">
        <button className="ub-btn" onClick={onPrimary}>
          See what&rsquo;s new
        </button>
        {onDismiss && (
          <button className="ub-dismiss" aria-label="Dismiss" onClick={onDismiss}>
            <Icon name="close" size={14} />
          </button>
        )}
      </span>
    </div>
  );
}
